import React from 'react';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import humanizeUtil from '@utils/humanizeUtil';
import { LIVE_EXCLUDE_METRICS } from '@utils/metricLens/mapping';
import { historicalRangeTableDataSortSelector } from '@store/metricLens/historicalRangeData/selectors';
import { liveRangeDataSelector } from '@store/metricLens/liveData/selectors';
import {
  selectedMetricsSelector,
  appliedSortSelector,
  selectedDimensionSelector,
  unitModeSelector,
  liveModeSelector,
  appliedSearchSelector,
} from '@store/metricLens/base/selectors';
import { getPercentageMetricByKey, getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import Header from './Header';
import Cell from './Cell';
import RowHeader from './RowHeader';
import DimensionHeader from './DimensionHeader';
import { atom } from 'jotai';

// main column (left, frozen)
const mainColSelector: any = createSelector(selectedDimensionSelector, (dimension) => {
  const columns = [
    {
      title: dimension,
      dataKey: 'name',
      key: dimension,
      width: 300,
      frozen: true,
      resizable: true,
      headerRenderer: () => <DimensionHeader />,
      cellRenderer: ({ cellData, rowData }) => <RowHeader text={cellData} rowData={rowData} />,
      align: 'left',
    },
  ];
  return columns;
});

// remaining metric based columns
export const metricColumnSelector = createSelector(
  selectedMetricsSelector,
  unitModeSelector,
  liveModeSelector,
  (metricList, unit, liveMode) => {
    if (liveMode) {
      const excludeList = LIVE_EXCLUDE_METRICS;
      excludeList.forEach((exclude) => {
        if (metricList.includes(exclude)) {
          metricList.splice(metricList.indexOf(exclude), 1);
        }
      });
    }
    const withInvertedMetrics = metricList.map((m) => {
      const invertedMetric = getPercentageMetricByKey({ metricKey: m });
      if (unit === '%' && invertedMetric) {
        return invertedMetric;
      }
      return m;
    });
    const tableColumns = withInvertedMetrics.map((metric) => {
      const metricInfo = getMetricMetaByKey({ metricKey: metric });
      return {
        title: metricInfo.short,
        dataKey: metric,
        key: metric,
        width: 300,
        headerRenderer: () => <Header metric={metricInfo} />,
        cellRenderer: ({ cellData, column, rowData, ...rest }) => (
          <Cell
            cellData={cellData}
            column={column}
            rowData={rowData}
            unit={metricInfo.unit}
            {...rest}
          />
        ),
        align: 'left',
      };
    });
    return tableColumns;
  },
);

// combined columns
export const columnsSelector = createSelector(
  mainColSelector,
  metricColumnSelector,
  (mainColumns, metricColumns) => [...mainColumns, ...metricColumns],
);

export const humanizeValue = (number: number, unit: string) => humanizeUtil(number, '', unit);

export const historicalRangeDataSelector = createSelector(
  historicalRangeTableDataSortSelector,
  liveRangeDataSelector,
  selectedMetricsSelector,
  appliedSortSelector,
  liveModeSelector,
  appliedSearchSelector,
  (historicalData, liveRangeData, metrics, sort, liveMode, appliedSearch) => {
    const expectedData = liveMode ? liveRangeData : historicalData;
    let sortedTableList = expectedData[sort.filter];
    if (sortedTableList) {
      if (appliedSearch !== '') {
        sortedTableList = sortedTableList.filter((row) => {
          const name = get(row, ['name', 0]);
          if (name.toLowerCase().includes(appliedSearch.toLowerCase())) {
            return true;
          }
          return false;
        });
      }
      const tableData: Array<{ name: string; id: string; value: string }> = [];
      const invertedMetrics: string[] = [];
      metrics.forEach((m) => {
        const invertedMetric = getPercentageMetricByKey({ metricKey: m });
        if (invertedMetric) {
          invertedMetrics.push(invertedMetric);
        }
      });
      const fullMetrics = [...metrics, ...invertedMetrics];
      sortedTableList.forEach((filterData) => {
        const rowData = {
          name: filterData.name,
          id: filterData.id,
          value: filterData.value,
        };
        fullMetrics.forEach((metric) => {
          rowData[metric] = [];
        });
        expectedData.forEach((eachData) => {
          const data = eachData.find((obj) => obj.id === filterData.id);
          fullMetrics.forEach((metric) => {
            rowData[metric].push(data[metric]);
          });
        });
        tableData.push(rowData);
      });
      return tableData;
    }
    return [];
  },
);

/**
 * state dragging, some action should no happen during dragging process.
 */
export const draggingAtom = atom(false);
